import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { CartItem as CartItemType, useCart } from "@/contexts/CartContext";
import { 
  calculateDays, 
  calculateNights
} from "@/utils/permitUtils";
import { 
  Tent, MapPin, Calendar, Info, Trash2, User, Mail, 
  Phone, Car, Dog, Edit3, Fish, Target, Warehouse, Tag 
} from "lucide-react";
import { useNavigate } from 'react-router-dom';
import EditItemModal from "./EditItemModal";
import { useIsMobile } from "@/hooks/use-mobile";

interface CartItemProps {
  item: CartItemType;
  onRemove: (id: string) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

export const CartItem: React.FC<CartItemProps> = ({ 
  item, 
  onRemove, 
  isExpanded, 
  onToggleExpand 
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { updateItem } = useCart();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [campingTotal, setCampingTotal] = useState(0);
  const [fishingPrice, setFishingPrice] = useState(0);
  const isMobile = useIsMobile();
  
  // Helper functions for item display
  const getItemIcon = (item: CartItemType) => {
    if (item.type === "fishing") {
      return <Fish className="h-4 w-4 mr-1 text-blue-600" />;
    } else if (item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping") {
      return <Tent className="h-4 w-4 mr-1 text-green-600" />;
    } else if (item.type === "hunting") {
      return <Target className="h-4 w-4 mr-1 text-amber-600" />;
    }
    return <Warehouse className="h-4 w-4 mr-1 text-tribal-primary" />;
  };

  const getItemTitle = (item: CartItemType) => {
    if (item.type === "fishing") {
      return `${item.permitName}`;
    } else if (item.type === "camping" || item.type === "rv-booking") {
      return item.permitName.startsWith('RV Site') 
        ? `RV Site: ${item.permitName.replace('RV Site - ', '')}`
        : `Camping Site: ${item.permitName}`;
    } else if (item.type === "general-camping") {
      return `General Camping: ${item.permitName}`;
    } else if (item.type === "hunting") {
      return `${item.permitName}`;
    }
    return "Unknown Item";
  };
  
  // Enhanced location display for camping items
  const getLocationDisplay = () => {
    if (item.type !== "camping" && item.type !== "rv-booking" && item.type !== "general-camping") return null;
    
    const locationText = item.location || (item.state && item.city ? `${item.city}, ${item.state}` : "Location details");
    
    return (
      <p className="text-xs flex items-center mt-1 text-tribal-secondary">
        <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
        <span className="truncate">{locationText}
        {item.siteNumber && ` - Site #${item.siteNumber}`}</span>
      </p>
    );
  };

  // Use stored prices - don't recalculate them
  useEffect(() => {
    if (item.type === "fishing") {
      // Trust the stored campingTotal from when the item was added
      const storedCampingTotal = item.campingTotal || 0;
      setCampingTotal(storedCampingTotal);
      
      // Calculate fishing price as the difference between total and camping
      const calculatedFishingPrice = item.totalPrice - storedCampingTotal;
      setFishingPrice(calculatedFishingPrice);
      
      console.log(`[CartItem] Price breakdown for ${item.id} (using stored values):`, {
        permitName: item.permitName,
        storedDays: item.days,
        storedTotalPrice: item.totalPrice,
        storedCampingTotal: storedCampingTotal,
        derivedFishingPrice: calculatedFishingPrice
      });
    }
  }, [item]);

  // Handle edit modal
  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  // New function to handle editing by navigation
  const handleEditByNavigation = () => {
    // Navigate to appropriate permit form based on item type
    if (item.type === "fishing") {
      navigate('/permits/fishing', { state: { editItem: item } });
    } else if (item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping") {
      navigate('/permits/camping', { state: { editItem: item } });
    } else if (item.type === "hunting") {
      navigate('/permits/hunting', { state: { editItem: item } });
    } else {
      // Fallback for unknown types
      toast({
        title: "Edit not supported",
        description: "Editing this item type is not yet supported.",
        variant: "destructive"
      });
    }
  };

  const handleEditSave = (id: string, updatedItem: Partial<CartItemType>) => {
    updateItem(id, updatedItem);
    setIsEditModalOpen(false);
  };

  // Use our utility functions for calculations
  // Check if fishing permit has camping add-on
  const hasCampingAddon = item.type === "fishing" && item.includesCamping === true;

  // Trust stored days first, only calculate from dates as fallback
  const days = item.days || (item.startDate && item.endDate ? calculateDays(item.startDate, item.endDate) : 0);
  // For camping nights, use dedicated camping dates if available
  const campingStartForDisplay = item.campingStartDate ? new Date(item.campingStartDate as any) : item.startDate;
  const campingEndForDisplay = item.campingEndDate ? new Date(item.campingEndDate as any) : item.endDate;
  const campingNights = campingStartForDisplay && campingEndForDisplay ? calculateNights(campingStartForDisplay, campingEndForDisplay) : 0;
  
  // Calculate camping price when part of a fishing permit - fixed rate of $15 per night
  // If same day camping (0 nights), charge for 1 night
  const effectiveCampingNights = hasCampingAddon && campingNights === 0 ? 1 : campingNights;

  // Generate simplified price breakdown for non-expanded view
  const renderSimplifiedPriceBreakdown = () => {
    if (item.type === "fishing") {
      // For fishing permits, show fishing and camping prices separately
      if (hasCampingAddon && campingTotal > 0) {
        return (
          <div className="text-xs text-tribal-neutral flex flex-col items-end">
            <span>Fishing: ${fishingPrice.toFixed(2)}</span>
            <span>Camping: ${campingTotal.toFixed(2)}</span>
          </div>
        );
      } else {
        // For fishing permits without camping, just show the fishing price
        return (
          <div className="text-xs text-tribal-neutral">
            <span>{days} day{days !== 1 ? 's' : ''} @ ${(fishingPrice / (days || 1)).toFixed(2)}/day</span>
          </div>
        );
      }
    } else if (item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping") {
      // For camping permits
      return (
        <div className="text-xs text-tribal-neutral">
          <span>${item.basePrice}/night × {campingNights} nights</span>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Collapsible 
        open={isExpanded}
        onOpenChange={onToggleExpand}
        className="w-full"
      >
        <Card className="bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0"> {/* min-width: 0 is crucial for text overflow */}
                <h3 className="font-semibold text-tribal-secondary flex items-center">
                  {getItemIcon(item)}
                  <span className="truncate">{getItemTitle(item)}</span>
                </h3>
                <div className="text-sm text-tribal-neutral">
                  <p className="truncate">
                    {format(item.startDate, 'MMM dd, yyyy')} - {format(item.endDate, 'MMM dd, yyyy')}
                  </p>
                  <p className="text-xs flex items-center mt-1">
                    <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                    {item.type === "fishing" ? (
                      <span>{days} day{days !== 1 ? "s" : ""} (inclusive)</span>
                    ) : (
                      <span>{campingNights} night{campingNights !== 1 ? "s" : ""}</span>
                    )}
                  </p>
                </div>
                {getLocationDisplay()}
                <div className="mt-2 min-w-0"> {/* min-width: 0 is crucial for text overflow */}
                  <p className="text-sm truncate">{item.customerName}</p>
                  <p className="text-xs text-tribal-neutral truncate">{item.customerEmail}</p>
                </div>
              </div>
              <div className="flex flex-col items-end ml-2 flex-shrink-0">
                <p className="font-semibold">
                  ${item.totalPrice.toFixed(2)}
                </p>
                
                {/* Add simplified price breakdown */}
                {renderSimplifiedPriceBreakdown()}
                
                <div className="flex items-center mt-2">
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-tribal-primary p-0 h-auto hover:text-tribal-primary/90 hover:bg-transparent"
                    >
                      <Info className="h-4 w-4 mr-1" />
                      <span className="text-xs">{isExpanded ? "Hide" : "View"}</span>
                    </Button>
                  </CollapsibleTrigger>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onRemove(item.id)}
                    className="text-red-500 p-0 h-auto ml-3 hover:text-red-700 hover:bg-transparent"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    <span className="text-xs">Remove</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Camping Add-on Display (when it's part of a fishing permit) with Price */}
            {hasCampingAddon && (
              <div className="mt-4 pt-3 border-t border-dashed border-tribal-primary/20">
                <div className="bg-green-50 rounded-md p-3 flex items-start overflow-hidden">
                  <Tent className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0"> {/* min-width: 0 is crucial for text overflow */}
                    <div className="flex justify-between items-start">
                      <div className="min-w-0"> {/* min-width: 0 is crucial for text overflow */}
                        <p className="text-sm font-medium text-green-800 flex items-center">
                          <span className="truncate">General Camping Add-on</span>
                          <Tag className="h-3 w-3 ml-2 text-green-600 flex-shrink-0" />
                        </p>
                        <p className="text-xs text-green-700 truncate">
                          {format(campingStartForDisplay, 'MMM dd, yyyy')} - {format(campingEndForDisplay, 'MMM dd, yyyy')}
                          <span className="ml-2">
                            ({effectiveCampingNights} night{effectiveCampingNights !== 1 ? 's' : ''} @ $15/night)
                          </span>
                        </p>
                      </div>
                      <div className="text-sm font-medium text-green-800 ml-1 flex-shrink-0">
                        ${campingTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <CollapsibleContent>
              <div className="mt-4 pt-4 border-t border-gray-100">
                {/* Responsive grid for expanded details */}
                <div className={`grid ${isMobile ? "grid-cols-1" : "md:grid-cols-2"} gap-4`}>
                  <div className="overflow-hidden">
                    <h4 className="text-sm font-medium mb-2 flex items-center">
                      <User className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                      Customer Information
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center">
                        <Mail className="h-3 w-3 mr-2 text-tribal-neutral flex-shrink-0" />
                        <span className="text-tribal-neutral mr-2">Email:</span>
                        <span className="break-all text-xs">{item.customerEmail}</span>
                      </div>
                      {item.phoneNumber && (
                        <div className="flex items-center">
                          <Phone className="h-3 w-3 mr-2 text-tribal-neutral flex-shrink-0" />
                          <span className="text-tribal-neutral mr-2">Phone:</span>
                          <span className="text-xs">{item.phoneNumber}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {(item.city || item.state || item.zipCode) && (
                    <div className="overflow-hidden">
                      <h4 className="text-sm font-medium mb-2 flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                        Address Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        {item.address && (
                          <div className="flex flex-wrap">
                            <span className="text-tribal-neutral mr-2">Address:</span>
                            <span className="break-all text-xs">{item.address}</span>
                          </div>
                        )}
                        <div className="flex flex-wrap">
                          {item.city && <span className="text-xs">{item.city}</span>}
                          {item.city && item.state && <span className="text-xs">, </span>}
                          {item.state && <span className="text-xs">{item.state} </span>}
                          {item.zipCode && <span className="text-xs">{item.zipCode}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Enhanced camping information display */}
                  {((item.type === "camping" || item.type === "rv-booking" || item.type === "general-camping") || hasCampingAddon) && (
                    <div className={isMobile ? "" : "col-span-2"}>
                      <h4 className="text-sm font-medium mb-2 flex items-center">
                        <Tent className="h-4 w-4 mr-1 text-green-600 flex-shrink-0" />
                        Camping Details
                      </h4>
                      <div className={`${hasCampingAddon ? "bg-green-50 p-3 rounded-md" : ""} 
                        ${isMobile ? "grid grid-cols-1 gap-1" : "grid grid-cols-2 gap-2"} text-sm overflow-hidden`}>
                        {item.siteId && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">Site ID:</span>
                            <span className="text-xs">{item.siteId}</span>
                          </div>
                        )}
                        {item.siteNumber && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">Site Number:</span>
                            <span className="text-xs">{item.siteNumber}</span>
                          </div>
                        )}
                        {item.campType && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">Type:</span>
                            <span className="text-xs">{item.campType.charAt(0).toUpperCase() + item.campType.slice(1)}</span>
                          </div>
                        )}
                        {item.guestCount && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">Guests:</span>
                            <span className="text-xs">{item.guestCount}</span>
                          </div>
                        )}
                        {item.rvType && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">RV Type:</span>
                            <span className="text-xs">{item.rvType}</span>
                          </div>
                        )}
                        {item.rvLength && (
                          <div className="truncate">
                            <span className="text-tribal-neutral mr-2">RV Length:</span>
                            <span className="text-xs">{item.rvLength} ft</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {item.licensePlate && (
                    <div className="overflow-hidden">
                      <h4 className="text-sm font-medium mb-2 flex items-center">
                        <Car className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                        Vehicle Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="truncate">
                          <span className="text-tribal-neutral mr-2">License Plate:</span>
                          <span className="text-xs">{item.licensePlate}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {item.bringingPets !== undefined && (
                    <div className="overflow-hidden">
                      <h4 className="text-sm font-medium mb-2 flex items-center">
                        <Dog className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                        Additional Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="truncate">
                          <span className="text-tribal-neutral mr-2">Bringing Pets:</span>
                          <span className="text-xs">{item.bringingPets ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {item.boatInspectionAcknowledged !== undefined && item.type === "fishing" && (
                    <div className="overflow-hidden">
                      <h4 className="text-sm font-medium mb-2 flex items-center">
                        <Dog className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                        Additional Information
                      </h4>
                      <div className="space-y-1 text-sm">
                        <div className="truncate">
                          <span className="text-tribal-neutral mr-2">Boat Inspection Acknowledged:</span>
                          <span className="text-xs">{item.boatInspectionAcknowledged ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Enhanced price breakdown for fishing+camping - IMPROVED MOBILE LAYOUT */}
                {item.type === "fishing" && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-md overflow-hidden">
                    <h4 className="text-sm font-medium mb-2 flex items-center">
                      <Info className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                      Price Breakdown
                    </h4>
                    
                    {/* New mobile-friendly price breakdown layout */}
                    <div className="space-y-2">
                      {/* Fishing permit line */}
                      <div className={`${isMobile ? "flex flex-col" : "flex justify-between items-center"}`}>
                        <div className="flex items-center truncate mb-1">
                          <Fish className="h-3 w-3 mr-1 text-blue-600 flex-shrink-0" />
                          <span className={`${isMobile ? "text-xs font-medium" : "text-sm"} break-words`}>
                            {item.permitName.startsWith("Daily") || !item.isAnnual ? 
                              `Fishing Permit (${days} day${days !== 1 ? 's' : ''} @ $${(fishingPrice / (days || 1)).toFixed(2)}/day)` : 
                              `Fishing Permit (Annual)`}
                          </span>
                        </div>
                        <span className={`${isMobile ? "self-end text-sm font-medium" : "flex-shrink-0"}`}>
                          ${fishingPrice.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Camping add-on line */}
                      {hasCampingAddon && (
                        <div className={`${isMobile ? "flex flex-col border-t border-gray-200 pt-2" : "flex justify-between items-center"}`}>
                          <div className="flex items-center truncate mb-1">
                            <Tent className="h-3 w-3 mr-1 text-green-600 flex-shrink-0" />
                            <span className={`${isMobile ? "text-xs font-medium" : "text-sm"} break-words`}>
                              Camping Add-on ({effectiveCampingNights} night{effectiveCampingNights !== 1 ? 's' : ''} @ $15/night)
                            </span>
                          </div>
                          <span className={`${isMobile ? "self-end text-sm font-medium" : "flex-shrink-0"}`}>
                            ${campingTotal.toFixed(2)}
                          </span>
                        </div>
                      )}
                      
                      {/* Total line - more prominent on mobile */}
                      <div className={`${isMobile ? "mt-3" : "mt-2"} border-t border-gray-300 pt-2`}>
                        <div className={`${isMobile ? "flex justify-between items-center bg-gray-100 p-2 rounded" : "flex justify-between"}`}>
                          <span className={`${isMobile ? "font-semibold" : "font-medium"}`}>Total</span>
                          <span className={`${isMobile ? "font-semibold text-tribal-primary" : "font-medium"}`}>
                            ${item.totalPrice.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Explanatory note - improved readability on mobile */}
                      {hasCampingAddon && (
                        <p className={`${isMobile ? "text-[11px] mt-2" : "text-xs mt-2"} text-tribal-neutral italic`}>
                          Note: Fishing days include both start and end dates. Camping nights are counted between dates.
                          {isMobile ? <br/> : " "}
                          Same-day camping counts as 1 night.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Price breakdown for camping-only permits */}
                {(item.type === "camping" || item.type === "general-camping") && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-md overflow-hidden">
                    <h4 className="text-sm font-medium mb-2 flex items-center">
                      <Info className="h-4 w-4 mr-1 text-tribal-primary flex-shrink-0" />
                      Price Breakdown
                    </h4>
                    
                    {/* New mobile-friendly price breakdown layout for camping */}
                    <div className="space-y-2">
                      {/* Camping fee line */}
                      <div className={`${isMobile ? "flex flex-col" : "flex justify-between items-center"}`}>
                        <div className="flex items-center truncate mb-1">
                          <Tent className="h-3 w-3 mr-1 text-green-600 flex-shrink-0" />
                          <span className={`${isMobile ? "text-xs font-medium" : "text-sm"} break-words`}>
                            {item.permitName} ({campingNights} night{campingNights !== 1 ? 's' : ''} @ ${item.basePrice.toFixed(2)}/night)
                          </span>
                        </div>
                        <span className={`${isMobile ? "self-end text-sm font-medium" : "flex-shrink-0"}`}>
                          ${item.totalPrice.toFixed(2)}
                        </span>
                      </div>
                      
                      {/* Total line - more prominent on mobile */}
                      <div className={`${isMobile ? "mt-3" : "mt-2"} border-t border-gray-300 pt-2`}>
                        <div className={`${isMobile ? "flex justify-between items-center bg-gray-100 p-2 rounded" : "flex justify-between"}`}>
                          <span className={`${isMobile ? "font-semibold" : "font-medium"}`}>Total</span>
                          <span className={`${isMobile ? "font-semibold text-tribal-primary" : "font-medium"}`}>
                            ${item.totalPrice.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-4 flex flex-wrap justify-end">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-tribal-neutral mb-1 mr-2"
                    onClick={handleEditClick}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Quick Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-tribal-primary mb-1"
                    onClick={handleEditByNavigation}
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Full Edit
                  </Button>
                </div>
              </div>
            </CollapsibleContent>
          </CardContent>
        </Card>
      </Collapsible>
      
      {/* Edit Modal */}
      <EditItemModal
        item={item}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
    </>
  );
};
